import appInfo from "@common/appInfo";
import { getTourAttributes } from "@common/contexts/Tour";
import Hint from "@common/controls/Hint/component";
import HelpIcon from "@common/controls/Icon/Help/component";
import Icon from "@common/controls/Icon/component";
import { type OnDropArgs } from "@common/controls/TreeView/Dnd/Inner/component";
import { IconType, type SortInfo } from "@common/declarations";
import { type SortEnd } from "@common/hocs/dnd";
import { useI18n } from "@common/hooks/i18n";
import { moveItem } from "@common/lib/array";
import classnames from "classnames";
import { isNull } from "lodash";
import React from "react";
import ErrorBoundary from "../ErrorBoundary/component";
import { FancyCheckbox } from "../Inputs/Checkbox/component";
import { DelayPreloader } from "../Preloader/component";
import { type TreeViewExpandMode } from "../TreeView/Dnd/Inner/types";
import RowList, { DndTreeRowList, MovableRowList, TreeRowList } from "./RowList";
import { hideEmptyColumns, isColumnSortable, isColumnVisible, isGroupItem } from "./lib";
import { type GridColumn, type GridGroupInfo, type PagerType, type SortEvent, type TablePaddings } from "./types";

export type NoDataMode = "add" | "hidden" | "grid" | "title";

export interface GridProps<T extends {}> {
  /** For DnD tree view only */
  activeId?: number | string;
  className?: string;
  columns: GridColumn<T>[];
  data: Array<T | GridGroupInfo>;
  expandMode?: TreeViewExpandMode;
  filterFn?: (text: string) => Promise<void> | T[] | void;
  fixed?: boolean;
  gridGroupMenu?: (rowData: GridGroupInfo) => React.ReactNode;
  gridRowMenu?: (rowData: T) => React.ReactNode;
  hasGroupMenu?: boolean;
  hasMenu?: boolean;
  hideEmptyColumns?: boolean;
  hint?: string;
  getRowClassName?: (rowData: T) => string | undefined;
  idKey: Extract<keyof T, string>;
  /** indicates whether the data is paginated, sorted, filtered by the BE */
  isBeDriven?: boolean;
  isExport?: boolean;
  isHeadless?: boolean;
  isLoading: boolean;
  isMenuAvailable?: (rowData: T) => boolean;
  isRowHighlighted?: (rowData: T) => boolean;
  noDataMode?: NoDataMode;
  noDataText?: string;
  noTourStep?: string;
  onCheckboxClick?: (value: boolean, items?: T[]) => void;
  onNoDataClick?: () => void;
  onRowClick?: (rowData: T) => void;
  onRowMove?: (sortEvent: SortEvent<T>) => void;
  paddings?: TablePaddings;
  pagerType?: PagerType;
  parentIdProp?: Extract<keyof T, string | number>;
  preHeader?: T[] | null;
  preHeaderGap?: number;
  renderGroup?: (data: GridGroupInfo) => any;
  selectedItems?: T[];
  sortedBy?: SortInfo<T>;
  sortFn?: (info: SortInfo<T>) => Promise<void> | void;
  tooltip?: (item: T) => string | null;
}

interface State<T> {
  data: Array<T | GridGroupInfo>;
}

const InnerGrid = <T extends {}>(props: GridProps<T>) => {
  const i18n = useI18n();

  const [state, setState] = React.useState<State<T>>({
    data: props.data
  });

  React.useEffect(() => {
    setState((x) => ({ ...x, data: props.data }));
  }, [props.data]);

  const getIsColumnSortable = (column: GridColumn<T>): boolean => {
    if (props.isExport || (props.isBeDriven && !column.isBESortable) || !isColumnSortable(column)) return false;
    return true;
  };

  const getOnSort = (key: Extract<keyof T, string>) => {
    return () => {
      if (props.onRowMove || !props.sortFn) return false;

      const column = props.columns.find((x) => x.key === key)!;
      if (!getIsColumnSortable(column)) return;

      props.sortFn({
        key,
        order: props.sortedBy?.order === "asc" && key === props.sortedBy.key ? "desc" : "asc",
        type: column.sortType ?? "default"
      });
    };
  };

  const onTreeDrop = React.useCallback(
    (args: OnDropArgs<T>) => {
      if (props.onRowMove) {
        props.onRowMove({
          item: args.source,
          parentId: args.parentId as Extract<keyof T, string | number>,
          index: args.index,
          // TODO: improve. The below should be removed, or unified with flat DnD args
          prevItem: null,
          nextItem: null,
          order: props.sortedBy?.order ?? "asc"
        });
      }

      setState((x) => ({
        ...x,
        data: args.items
      }));
    },
    [props.onRowMove, props.parentIdProp]
  );

  const moveEnd = React.useCallback(
    (e: SortEnd) => {
      if (state.data.length === 1) return;
      if (e.newIndex === e.oldIndex) return;

      let data = moveItem(state.data.slice(), e);

      if (props.onRowMove) {
        const item = data[e.newIndex] as T;
        const prevItem = e.newIndex > 0 ? (data[e.newIndex - 1] as T) : null;
        const nextItem = e.newIndex < data.length - 1 ? (data[e.newIndex + 1] as T) : null;

        // if moving rows is allowed, then the data must be sorted
        props.onRowMove({
          index: e.newIndex,
          item,
          prevItem,
          nextItem,
          order: props.sortedBy?.order ?? "asc"
        });
      }

      setState((x) => ({
        ...x,
        data
      }));
    },
    [props.onRowMove, state.data, props.sortedBy?.order]
  );

  const ref = React.useRef<HTMLTableElement>(null);

  // RowList depends on all props, thus no hook is used
  const renderTable = (data: Array<T | GridGroupInfo>) => {
    const visibleColumns = props.columns.filter(isColumnVisible);
    const isHeadless = !!props.isHeadless || !visibleColumns.some((x) => x.name);
    const hasGroup = props.data.some((x) => isGroupItem(x));

    const table = (
      <table
        ref={ref}
        className={classnames(`table table-striped paddings_${props.paddings ?? "all"}`, {
          fixed: props.fixed
        })}
        style={{ width: "100%" }}
      >
        {(!isHeadless || !!props.preHeader) && (
          <thead>
            {props.preHeader && <RowList {...props} data={props.preHeader} />}
            {!!props.preHeaderGap && (
              <tr
                style={{
                  height: props.preHeaderGap
                }}
              >
                {!!props.onCheckboxClick && !!props.selectedItems && (
                  <th key="checkbox" className="head_checkbox">
                    <FancyCheckbox
                      value={props.data.every((x) =>
                        props.selectedItems!.some((y) => y[props.idKey] === (x as T)[props.idKey])
                      )}
                      onChange={(data) => {
                        props.onCheckboxClick!(data ?? false, props.data as T[]);
                      }}
                    />
                  </th>
                )}
                {visibleColumns.map((col) => (
                  <th key={col.key as string} />
                ))}
                {props.hasMenu && <th className="actions cell" />}
              </tr>
            )}

            {!isHeadless && (
              <tr className="active">
                {/* regular draggable row list needs an extra column */}
                {!!props.onRowMove && (!props.parentIdProp || hasGroup) && data.length > 1 && <th />}
                {!!props.onCheckboxClick && !!props.selectedItems && (
                  <th key="checkbox" className="head_checkbox">
                    <FancyCheckbox
                      value={props.data.every((x) =>
                        props.selectedItems!.some((y) => y[props.idKey] === (x as T)[props.idKey])
                      )}
                      onChange={(data) => {
                        props.onCheckboxClick!(data ?? false, props.data as T[]);
                      }}
                    />
                  </th>
                )}
                {visibleColumns.map((col) => (
                  <th
                    key={col.key as string}
                    onClick={getOnSort(col.key)}
                    className={classnames({
                      sortable: !props.onRowMove && props.sortFn && props.sortedBy && getIsColumnSortable(col),
                      sorted: col.key === props.sortedBy?.key
                    })}
                    style={{
                      textAlign: col.align || "left",
                      whiteSpace: "nowrap",
                      width: "initial"
                    }}
                  >
                    <div
                      className="cell_content single-line-ellipsis"
                      style={{
                        justifyContent: col.align === "right" ? "end" : "start",
                        whiteSpace: "nowrap",
                        width: "initial"
                      }}
                    >
                      {col.name}
                      {isNull(col.help) ? null : col.help ? (
                        <HelpIcon tooltip={col.help} />
                      ) : col.dataType === "dateLocal" || col.dataType === "dateTimeLocal" ? (
                        <HelpIcon tooltip={appInfo.business.timeZone} />
                      ) : col.dataType === "date" || col.dataType === "dateTime" ? (
                        <HelpIcon tooltip={i18n.utc} />
                      ) : null}
                      {getIsColumnSortable(col) && (
                        <Icon
                          className="sort"
                          icon={props.sortedBy?.order === "asc" ? IconType.ChevronDown : IconType.ChevronUp}
                        />
                      )}
                    </div>
                  </th>
                ))}
                {props.hasMenu && <th className="actions cell" />}
              </tr>
            )}
          </thead>
        )}
        <tbody>
          {props.onRowMove ? (
            props.parentIdProp ? (
              <DndTreeRowList {...props} data={data} parentIdProp={props.parentIdProp} onDrop={onTreeDrop} />
            ) : (
              <MovableRowList onSortEnd={moveEnd} useHandles {...props} data={data} />
            )
          ) : props.parentIdProp ? (
            <TreeRowList {...props} data={data} parentIdProp={props.parentIdProp} />
          ) : (
            <RowList {...props} columns={visibleColumns} data={data} />
          )}
        </tbody>
      </table>
    );

    return table;
  };

  React.useLayoutEffect(() => {
    if (!props.hideEmptyColumns) return;

    ref.current && hideEmptyColumns(ref.current);
  });

  if (props.isExport) {
    return renderTable(state.data);
  }

  const visibleColumns = props.columns.filter(isColumnVisible);
  const isHeadless = !!props.isHeadless || !visibleColumns.some((x) => x.name);
  const isNoData = !state.data?.length;

  if (isNoData && props.noDataMode === "hidden") return null;

  return (
    <div
      className={classnames("grid", props.className, {
        headless: isHeadless,
        loading: props.isLoading && props.pagerType !== "scroll"
      })}
    >
      {!!props.hint && <Hint hint={props.hint} />}

      {/* NOTE: when there's no data we should render an overlay, but it should consider the one-row grid height */}
      {isNoData
        ? props.noDataMode
          ? renderTable([{ id: "fake", label: "", value: "dummy" }])
          : null
        : renderTable(state.data)}

      {isNoData && (!!props.noDataMode || !props.hint) && (
        <div
          className={classnames("no_data", {
            cover: props.noDataMode !== "grid",
            clickable: props.noDataMode === "add" && props.onNoDataClick
          })}
          onClick={props.noDataMode === "add" ? props.onNoDataClick : undefined}
          {...getTourAttributes({ step: props.noDataMode === "add" ? props.noTourStep : undefined })}
        >
          {props.noDataMode === "add" && <Icon icon={IconType.Add} />}
          <span className={classnames({ hint: props.noDataMode !== "add" })}>{props.noDataText ?? i18n.infoNoData}</span>
        </div>
      )}

      {props.isLoading && props.pagerType !== "scroll" && <DelayPreloader relative />}
    </div>
  );
};

// const UnsafeGrid = <T extends {}>(props: GridProps<T>) => {
//   if (isUniqBy(props.columns, "key")) {
//     throw new Error("BUG: Grid columns must have unique keys");
//   }
//   return <InnerGrid {...props} />;
// };

export const Grid = <T extends {}>(props: GridProps<T>) => (
  <ErrorBoundary>
    {/* TODO: use UnsafeGrid instead */}
    <InnerGrid {...props} />
  </ErrorBoundary>
);
